import { useState } from 'react';

/**
 * design-system/06-Components/01-Buttons.md — primary, pill, exactly one per screen.
 * Mirrors android/…/Components/PrimaryButton.kt and ios/…/Components/PrimaryButton.swift.
 *
 * While loading the width is held and the label is replaced by a spinner, so nothing reflows; the control
 * is non-interactive but is not styled as disabled.
 *
 * It is a real `<button>`. A styled `<div>` would drop keyboard focus, the accessible role and the enter
 * key, and the web is the one client where a keyboard is a primary input.
 */
export function PrimaryButton({
  label,
  onClick,
  enabled = true,
  loading = false,
  height = 'var(--touch-child)',
  fullWidth = true,
  type = 'button',
}: {
  label: string;
  onClick?: () => void;
  enabled?: boolean;
  loading?: boolean;
  height?: string;
  fullWidth?: boolean;
  type?: 'button' | 'submit';
}) {
  const [pressed, setPressed] = useState(false);
  const interactive = enabled && !loading;

  const background = !enabled
    ? 'var(--surface-sunken)'
    : pressed
      ? 'var(--action-primary-pressed)'
      : 'var(--action-primary)';

  return (
    <button
      type={type}
      disabled={!interactive}
      aria-busy={loading}
      onClick={onClick}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        minHeight: height,
        width: fullWidth ? '100%' : undefined,
        padding: 'var(--space-3) var(--space-6)',
        borderRadius: 'var(--radius-pill)',
        border: 'none',
        background,
        color: enabled ? 'var(--text-on-action)' : 'var(--text-disabled)',
        fontSize: '15px',
        lineHeight: '20px',
        fontWeight: 600,
        cursor: interactive ? 'pointer' : 'default',
        transition: 'background var(--motion-quick) ease',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {loading ? <Spinner /> : label}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: '2px solid rgba(255, 255, 255, 0.35)',
        borderTopColor: 'var(--text-on-action)',
        animation: 'spin 700ms linear infinite',
      }}
    />
  );
}
